import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ImageOff } from "lucide-react";
import { cn } from "../lib/cn";
import { Skeleton } from "./Skeleton";

/**
 * Image — sealed wrapper around `<img>` that owns the loading skeleton,
 * one-shot fallback retry, error chrome, and CLS-prevention slot.
 *
 * Defaults:
 * - `loading="lazy"` (native browser lazy-loading; flips to `eager` +
 *   `fetchpriority="high"` when `priority` is set).
 * - `decoding="async"` so the renderer can hand decode work to a worker
 *   thread instead of blocking the main thread.
 * - Skeleton overlay during load (covered by the existing `Skeleton`
 *   primitive — same shimmer + theme tokens consumers already see).
 * - Error state: `<ImageOff>` Lucide icon centered on `bg-muted`, with a
 *   tiny `errorLabel` rendered alongside when the wrapper is at least
 *   64px on the shorter side. Smaller wrappers get just the icon.
 * - Empty / undefined `src` renders the error state directly (does not
 *   render an empty `<img>`).
 *
 * Composition seam: consumer-provided `errorContent` overrides the
 * default error chrome. `fallbackSrc` provides one-shot retry; if the
 * fallback also errors (or none was given), the consumer's `onError`
 * fires AFTER the internal retry so they can run a fancier multi-step
 * fallback chain (AS's `experience-image-fallback` keeps working — if
 * the consumer changes `src` from their `onError`, the primitive's
 * `useEffect` resyncs to the new source).
 *
 * Layout:
 * - Pass `aspectRatio` (e.g. `"16/9"`) to set CSS `aspect-ratio` on the
 *   wrapper. Pairs with consumer-supplied width/height utilities on
 *   `className` to prevent CLS while the image is loading.
 * - The wrapper handles position + radius; the inner `<img>` inherits
 *   radius via `rounded-[inherit]` and fills the wrapper with `object-cover`.
 *
 * Motion: one 200ms opacity crossfade on load (`ease-smooth-out`).
 * Honors `prefers-reduced-motion` automatically via
 * `motion-reduce:transition-none`. No explicit `motion={false}` prop —
 * there's no spring or transform to opt out of.
 *
 * SSR: every state hook is in the function body, no module-top access
 * to `window` / `document`. Native `loading="lazy"` works without JS,
 * so server-rendered images load correctly even pre-hydration.
 *
 *   <Image src={url} alt="Hero" aspectRatio="16/9" priority />
 *
 *   <Image
 *     src={experience.cover}
 *     alt={experience.title}
 *     fallbackSrc="/static/cover-fallback.png"
 *     onError={(e) => analytics.imageFailed(experience.id)}
 *     aspectRatio="4/3"
 *     rounded="lg"
 *     className="w-full"
 *   />
 *
 *   <Image
 *     src={undefined}
 *     alt=""
 *     errorContent={<EmptyState title="No cover yet" />}
 *     aspectRatio="1/1"
 *   />
 */
export const imageVariants = cva("relative overflow-hidden bg-muted", {
  variants: {
    rounded: {
      none: "rounded-none",
      sm: "rounded-sm",
      default: "rounded-md",
      lg: "rounded-lg",
      full: "rounded-full",
    },
  },
  defaultVariants: { rounded: "default" },
});

type WrapperHTMLAttributes = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  // These collide with our typed props or don't apply to a wrapper div.
  "children"
>;

type NativeImgAttributes = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  // Owned by the primitive; consumer signals intent through `priority` instead.
  | "loading"
  | "decoding"
  // `fetchPriority` is camelCase in React's types but the runtime warns;
  // we emit the lowercase HTML attribute internally.
  | "fetchPriority"
  // Conflict with React's `Ref` type when forwardRef is in play.
  | "ref"
  // We pass `style` and `className` to the wrapper, not the inner img.
  | "style"
  | "className"
>;

export interface ImageProps extends NativeImgAttributes, VariantProps<typeof imageVariants> {
  /**
   * Image source URL. Empty / undefined renders the error state directly
   * (the primitive does not emit an empty `<img>` to the DOM).
   */
  src: string | undefined;

  /**
   * Required. Pass `""` deliberately for decorative images. Never inherit
   * a placeholder string ("loading…") — that becomes the announced text
   * for screen readers if the image errors.
   */
  alt: string;

  /**
   * CSS aspect ratio for the wrapper. Prevents layout shift while loading.
   * Examples: `"16/9"`, `"4/3"`, `"1/1"`, `"9/16"`. Forwarded to
   * `style.aspectRatio` on the wrapper. Pair with width utilities on
   * `className` for the constraint dimension.
   */
  aspectRatio?: string;

  /**
   * Mark this image as above-the-fold / LCP-critical. Switches loading
   * from `"lazy"` to `"eager"` and adds `fetchpriority="high"` so the
   * browser prioritises its byte-stream over other resources.
   *
   * Mistake mode: a lazy-loaded LCP image is one of the worst-case CLS
   * + LCP regressions; mark hero / hero-card images explicitly.
   */
  priority?: boolean;

  /**
   * Single-retry fallback URL. If `src` errors, the primitive swaps to
   * `fallbackSrc` once. If `fallbackSrc` also errors, the error state
   * renders. For multi-step fallback chains (e.g. AISquare's experience
   * type-default thumbnails), keep a parent-side `onError` handler that
   * mutates `src` — the primitive forwards `onError` after its own
   * retry, and resyncs when `src` changes.
   */
  fallbackSrc?: string;

  /**
   * Custom error-state content. Replaces the default `ImageOff` icon +
   * `errorLabel` chrome. Use when the surrounding card already conveys
   * "no image" via other affordances and the icon would be redundant.
   */
  errorContent?: React.ReactNode;

  /**
   * Label rendered alongside the error icon when the wrapper is at
   * least ~64px on the shorter side. Default: `"Image unavailable"`.
   */
  errorLabel?: string;

  /** Wrapper className. Use for sizing / positioning utilities. */
  className?: string;

  /** Wrapper inline style. Merges with `aspectRatio` (consumer wins). */
  style?: React.CSSProperties;

  /** Pass-through wrapper HTML attributes (data-*, aria-*, etc.). */
  wrapperProps?: WrapperHTMLAttributes;
}

const ERROR_LABEL_MIN_PX = 64;

export const Image = React.forwardRef<HTMLDivElement, ImageProps>(function Image(
  {
    src,
    alt,
    aspectRatio,
    priority = false,
    fallbackSrc,
    errorContent,
    errorLabel = "Image unavailable",
    className,
    style,
    rounded,
    wrapperProps,
    onLoad,
    onError,
    ...imgProps
  },
  forwardedRef,
) {
  const [currentSrc, setCurrentSrc] = React.useState<string | undefined>(src);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [errored, setErrored] = React.useState(!src);
  const [showErrorLabel, setShowErrorLabel] = React.useState(false);

  // Track whether we've already burned the one-shot fallback retry for the
  // current `src`. Resets when the parent passes a new `src`.
  const triedFallbackRef = React.useRef(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  // Resync when the consumer mutates `src` (including from inside their own
  // `onError` callback for multi-step fallback chains).
  React.useEffect(() => {
    setCurrentSrc(src);
    triedFallbackRef.current = false;
    if (!src) {
      setIsLoaded(false);
      setErrored(true);
    } else {
      setIsLoaded(false);
      setErrored(false);
      setShowErrorLabel(false);
    }
  }, [src]);

  const setContainerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef],
  );

  const handleLoad: React.ReactEventHandler<HTMLImageElement> = (event) => {
    setIsLoaded(true);
    onLoad?.(event);
  };

  const handleError: React.ReactEventHandler<HTMLImageElement> = (event) => {
    // Phase 1: one-shot internal fallback retry.
    if (fallbackSrc && !triedFallbackRef.current && currentSrc !== fallbackSrc) {
      triedFallbackRef.current = true;
      setCurrentSrc(fallbackSrc);
      setIsLoaded(false);
      return;
    }

    // Phase 2: notify the consumer. They may setState-mutate `src` from a
    // parent component to attempt a multi-step fallback; our `useEffect`
    // above resyncs on the next render commit.
    onError?.(event);

    // Phase 3: render the error chrome. If the consumer DID change `src`
    // upstream, the resync useEffect fires shortly after and clears
    // `errored` back to false. The intermediate render is one frame of
    // error UI that masks any flash of broken-image — acceptable cost.
    setErrored(true);
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setShowErrorLabel(Math.min(rect.width, rect.height) >= ERROR_LABEL_MIN_PX);
    }
  };

  const wrapperStyle: React.CSSProperties = {
    ...(aspectRatio ? { aspectRatio } : {}),
    ...style,
  };

  // For decorative images (`alt=""`), suppress the screen-reader error
  // announcement — the failure isn't user-relevant. For meaningful alt,
  // promote the wrapper to `role="img"` with the error label as the
  // announcement text so the wrapper still reads something useful.
  const errorAriaProps =
    errored && alt
      ? ({
          role: "img" as const,
          "aria-label": errorLabel,
        } as const)
      : ({ "aria-hidden": true } as const);

  return (
    <div
      ref={setContainerRef}
      className={cn(imageVariants({ rounded }), className)}
      style={wrapperStyle}
      aria-busy={!isLoaded && !errored ? true : undefined}
      {...wrapperProps}
    >
      {!isLoaded && !errored ? (
        <Skeleton
          variant="block"
          className="absolute inset-0 rounded-[inherit]"
          aria-hidden="true"
        />
      ) : null}

      {errored
        ? (errorContent ?? (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-muted-foreground"
              {...errorAriaProps}
            >
              <ImageOff className="size-6 opacity-60" aria-hidden="true" />
              {showErrorLabel ? (
                <span className="text-[10px] font-medium opacity-70">{errorLabel}</span>
              ) : null}
            </div>
          ))
        : null}

      {!errored && currentSrc ? (
        <img
          {...imgProps}
          // `fetchpriority` is the canonical HTML attribute (all lowercase
          // per spec). React 18 warns if you spell it `fetchPriority`
          // because the camelCase form isn't recognised on the legacy
          // events whitelist. Spread the lowercase form via a Record cast
          // so the runtime emits the correct attribute and no warning fires.
          {...(priority ? ({ fetchpriority: "high" } as Record<string, string>) : {})}
          src={currentSrc}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            "h-full w-full object-cover rounded-[inherit] transition-opacity duration-200 ease-smooth-out motion-reduce:transition-none",
            isLoaded ? "opacity-100" : "opacity-0",
          )}
        />
      ) : null}
    </div>
  );
});
Image.displayName = "Image";
